import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, Loader2, Share2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/Card';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import Button from '../components/ui/Button';
import { organizationsService, SELECTED_ORGANIZATION_EVENT } from '../services/organizationsService';
import usePermissions from '../hooks/usePermissions';
import { PERMISSIONS } from '../services/permissions';
import { FAST_ID_REPORT_LIST_PAGE_KEY, listReturnPath } from '../utils/listPage';
import {
  FAST_ID_DOMAIN_SLICE_ALL,
  FAST_ID_DOMAIN_SLICE_OPTIONS,
  FastIdReportSections,
  copyFastIdReportLink,
  downloadFastIdPurchase,
  fetchFastIdReports,
} from '../components/fastId/FastIdReportView';

const NOT_AUTHORIZED = "You're not authorized to view this report.";

function selectedOrganizationId() {
  const raw = organizationsService.getSelectedOrganization('id');
  if (raw == null || raw === '') return null;
  const id = Number(raw);
  return Number.isFinite(id) ? id : null;
}

function organizationIdFromSearch(searchParams) {
  const raw = searchParams.get('organization_id');
  if (raw == null || raw === '') return null;
  const id = Number(raw);
  return Number.isFinite(id) ? id : null;
}

async function organizationsForUser() {
  try {
    return await organizationsService.getOrganizations();
  } catch {
    return organizationsService.getOrganizationsFromCookie() || [];
  }
}

/** Org id from the selection cookie, only when it is an org this user belongs to. */
async function organizationIdUserBelongsTo() {
  const directId = selectedOrganizationId();
  const name = organizationsService.getSelectedOrganization('name');
  const orgs = await organizationsForUser();
  if (directId != null && orgs.some((org) => Number(org.id) === directId)) return directId;
  if (name) {
    const match = orgs.find((org) => org.name === name);
    const id = Number(match?.id);
    if (Number.isFinite(id)) return id;
  }
  return null;
}

export default function FastIdReportDetail() {
  const { can } = usePermissions();
  const canExport = can(PERMISSIONS.EXPORT);
  const { executionId } = useParams();
  const [searchParams] = useSearchParams();
  const urlOrganizationId = organizationIdFromSearch(searchParams);
  const location = useLocation();
  const navigate = useNavigate();
  const listPath = listReturnPath('/fastid-report', location, FAST_ID_REPORT_LIST_PAGE_KEY);

  const [organizationId, setOrganizationId] = useState(selectedOrganizationId);
  const [domainSlice, setDomainSlice] = useState(FAST_ID_DOMAIN_SLICE_ALL);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  /** Domain-slice refetch only: keep layout/data visible (no full-page loading flash). */
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const dataRef = useRef(null);
  const organizationIdRef = useRef(organizationId);
  dataRef.current = data;

  useEffect(() => {
    const syncOrganization = () => setOrganizationId(selectedOrganizationId());
    window.addEventListener(SELECTED_ORGANIZATION_EVENT, syncOrganization);
    return () => window.removeEventListener(SELECTED_ORGANIZATION_EVENT, syncOrganization);
  }, []);

  useEffect(() => {
    if (!executionId) return undefined;
    let cancelled = false;
    const organizationChanged = organizationIdRef.current !== organizationId;
    organizationIdRef.current = organizationId;
    const keepStaleLayout = dataRef.current != null && !organizationChanged;

    (async () => {
      if (urlOrganizationId != null) {
        const orgs = await organizationsForUser();
        if (cancelled) return;
        const match = orgs.find((org) => Number(org.id) === urlOrganizationId);
        if (!match) {
          setData(null);
          setError(NOT_AUTHORIZED);
          setLoading(false);
          setRefreshing(false);
          return;
        }
        if (selectedOrganizationId() !== urlOrganizationId) {
          organizationsService.setSelectedOrganization(String(match.id), 'id', orgs);
        }
        if (organizationId !== urlOrganizationId) {
          setOrganizationId(urlOrganizationId);
          return;
        }
      } else if (!organizationId) {
        const resolved = await organizationIdUserBelongsTo();
        if (cancelled) return;
        if (!resolved) {
          setData(null);
          setError(NOT_AUTHORIZED);
          setLoading(false);
          setRefreshing(false);
          return;
        }
        setOrganizationId(resolved);
        return;
      }
      if (keepStaleLayout) setRefreshing(true);
      else setLoading(true);
      setError(null);
      try {
        const result = await fetchFastIdReports(executionId, domainSlice, organizationId);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          setData(null);
          const status = err.response?.status;
          setError(
            status === 403 || status === 404
              ? NOT_AUTHORIZED
              : err.response?.data?.detail || err.message || 'Failed to load Fast-ID report.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [executionId, domainSlice, organizationId, urlOrganizationId]);

  const reportFileName = String(data?.report?.filename ?? '').trim();
  const pageTitle = reportFileName
    ? `FastID Report (${reportFileName})`
    : 'FastID Report';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => navigate(listPath)}
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
        >
          <ArrowLeft className="h-4 w-4" /> FastID Reports
        </button>
        <h1 className="text-xl font-semibold text-gray-900 break-all">{pageTitle}</h1>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-4 mb-4">
            <div className="min-w-0 flex-1">
              <span className="font-medium text-gray-700">Execution ID:</span>{' '}
              <code className="break-all bg-gray-100 px-2 py-0.5 rounded text-xs">
                {executionId}
              </code>
            </div>
            <div className="w-full sm:w-56 sm:flex-shrink-0">
              <FunctionalSelect
                label="Domain"
                id="fast-id-report-domain"
                value={domainSlice}
                onValueChange={setDomainSlice}
                placeholder="All"
                className="w-full"
              >
                {FAST_ID_DOMAIN_SLICE_OPTIONS.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </FunctionalSelect>
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => copyFastIdReportLink(executionId, organizationId ?? urlOrganizationId)}
              aria-label="Copy report link"
              title="Copy report link"
              className="sm:flex-shrink-0"
            >
              <Share2 className="h-4 w-4" />
            </Button>
            {data?.report?.purchase_url && canExport && (
              <Button
                type="button"
                variant="outline"
                onClick={() => downloadFastIdPurchase(executionId, organizationId)}
                aria-label="Download purchase file"
                title="Download purchase file"
                className="sm:flex-shrink-0 gap-2"
              >
                <Download className="h-4 w-4" />
                Purchase List
              </Button>
            )}
          </div>

          {loading && data == null && <p className="text-sm text-gray-600">Loading report…</p>}
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          {data != null && (
            <div className="relative text-sm text-gray-800 space-y-4">
              {refreshing && (
                <div
                  className="absolute inset-0 z-10 flex justify-center pt-6 bg-white/50 pointer-events-none rounded-md"
                  aria-busy="true"
                  aria-live="polite"
                >
                  <span className="inline-flex items-center gap-2 text-sm text-gray-600 h-fit">
                    <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
                    Updating…
                  </span>
                </div>
              )}
              <FastIdReportSections report={data?.report} matchRate={data?.matchRate} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
